import {
  BASE_WELL_LAYER,
  BASE_WELL_RASTER_LAYER,
  IPDB_CONTROL_WELLS_LAYER_TYPE,
  IPDB_LAYER_TYPE,
  RIG_FRAC_LAYER_TYPE,
  SELECTED_SINGLE_WELL_LAYER,
  SELECTED_SINGLE_WELL_POINT_LAYER,
  SELECTED_WELL_LAYER,
  SELECTED_WELL_POINT_LAYER,
  TYPE_LOG_LAYER,
  TYPE_LOG_VERTICAL_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT
} from "constants/mapLayers.constants";

import { IProjectLayer } from "components/project/layers/queries";

import {
  IEditableProjectLayer,
  LayerListFilterTypesT
} from "../../useEditableProjectLayerList";

export interface ICreateEditableLayerListParams {
  layerList: IProjectLayer[];
  checkedKeys: string[];
  filters?: LayerListFilterTypesT[];
  ipdbGeoModelLoaded?: boolean;
  ipdbControlWellsLoaded?: boolean;
  rigFracEnabled?: boolean;
  baseLayerRefactor?: boolean;
}

interface ICreateEditableLayerListReturn {
  list: IEditableProjectLayer[];
  checkedShapefiles: Set<string>;
  checkedProjectShapefileIds: string[];
  checkedProjectLinkedShapefileIds: string[];
}

// TODO: add dependant layers to the list

export function createEditableLayerList(
  params: ICreateEditableLayerListParams
): ICreateEditableLayerListReturn {
  const checkedProjectShapefileIds = [];
  const checkedProjectLinkedShapefileIds = [];
  const checkedShapefiles = new Set<string>();

  const newList: IEditableProjectLayer[] = [];
  params.layerList?.forEach((layer: IProjectLayer) => {
    if (layer.projectLayerType === "geomBin") {
      return;
    }

    // Filter out IPDB layers based on loaded status
    if (layer.projectLayerType === IPDB_LAYER_TYPE && !params.ipdbGeoModelLoaded) {
      return;
    }
    if (
      layer.projectLayerType === IPDB_CONTROL_WELLS_LAYER_TYPE &&
      !params.ipdbControlWellsLoaded
    ) {
      return;
    }

    // Filter out rig/frac layers unless the beta feature is enabled
    if (layer.projectLayerType === RIG_FRAC_LAYER_TYPE && !params.rigFracEnabled) {
      return;
    }
    const layerName = layer.projectShapefileId || layer.shapefileId || layer.name;
    const isChecked = params.checkedKeys.includes(layerName);

    if (params.checkedKeys.includes(layer.projectShapefileId)) {
      checkedProjectLinkedShapefileIds.push(layer.projectShapefileId);
      checkedShapefiles.add(layer.projectShapefileId);
    }

    if (params.checkedKeys.includes(layer.shapefileId)) {
      checkedProjectShapefileIds.push(layer.shapefileId);
      checkedShapefiles.add(layer.shapefileId);
    }

    addDependantMapLayers({ newList, isChecked, layer, layerName });

    newList.push({
      ...layer,
      isChecked: isChecked,
      isMoving: false,
      zoomLevel: 8,
      originalOrder: layer.order
    });

    // The base-well survey lines are a second mapbox layer paired with the
    // "Base Well" circle layer, and they must draw *below* it — which is
    // the order getMapLayers creates them in. The sorter reverses this list
    // before matching it to mapbox's own (bottom-up) order, so a row pushed
    // after its parent ends up beneath it. That's why this one row is added
    // here rather than in addDependantMapLayers, which pushes before the parent.
    if (layerName === BASE_WELL_LAYER && (params.baseLayerRefactor ?? true)) {
      newList.push({
        ...layer,
        dependantLayer: BASE_WELL_LAYER,
        isChecked,
        isMoving: false,
        layerId: `${BASE_WELL_LAYER}-lines`,
        name: `${BASE_WELL_LAYER}-lines`,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });

      // The raster crossover layer (EVA-35) is a THIRD mapbox layer this same
      // checkbox controls (see DEPENDENT_LAYERS.baseWell in
      // useUpdateMapLayersVisibility.tsx, which already treats it as a
      // dependant for visibility) — without a row here, reordering "Base
      // Well" in the tree moves the circle and lines layers together but
      // leaves the raster layer stranded wherever getMapLayers/MapboxWrapper
      // first inserted it.
      newList.push({
        ...layer,
        dependantLayer: BASE_WELL_LAYER,
        isChecked,
        isMoving: false,
        layerId: BASE_WELL_RASTER_LAYER,
        name: BASE_WELL_RASTER_LAYER,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });
    }
  });

  const filteredList = newList.filter((layer) => {
    if (!params.filters?.length) return true;

    const isHidingUnselectedLayer =
      params.filters.includes("unselectedLayers") && !layer.isChecked;

    const isDependantLayer =
      params.filters.includes("dependantLayers") && layer.dependantLayer;

    // If unselectedLayers filter is applied and layer is unchecked, filter it out
    if (isHidingUnselectedLayer) {
      return false;
    }

    // If dependantLayers filter is applied and layer has a dependent layer, filter it out
    if (isDependantLayer) {
      return false;
    }

    // Otherwise, include the layer
    return true;
  });

  const orderedList = filteredList.map((layer, index) => ({ ...layer, order: index }));

  return {
    list: orderedList,
    checkedProjectShapefileIds,
    checkedProjectLinkedShapefileIds,
    checkedShapefiles
  };
}

function addDependantMapLayers(params: {
  isChecked: boolean;
  layer: IProjectLayer;
  layerName: string;
  newList: IEditableProjectLayer[];
}) {
  const { layer, layerName, newList, isChecked } = params;

  if ([SELECTED_WELL_LAYER, WELL_LAYER].includes(layerName)) {
    if (layerName === SELECTED_WELL_LAYER) {
      // Its draw order is pinned by useManageMapboxLayerSorting, not this row.
      newList.push({
        ...layer,
        dependantLayer: SELECTED_WELL_LAYER,
        isChecked,
        isMoving: false,
        layerId: SELECTED_SINGLE_WELL_POINT_LAYER,
        name: SELECTED_SINGLE_WELL_POINT_LAYER,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });

      newList.push({
        ...layer,
        dependantLayer: SELECTED_WELL_LAYER,
        isChecked,
        isMoving: false,
        layerId: SELECTED_WELL_POINT_LAYER,
        name: SELECTED_WELL_POINT_LAYER,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });

      newList.push({
        ...layer,
        dependantLayer: SELECTED_WELL_LAYER,
        isChecked,
        isMoving: false,
        layerId: SELECTED_SINGLE_WELL_LAYER,
        name: SELECTED_SINGLE_WELL_LAYER,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });
    }

    if (layerName === WELL_LAYER) {
      newList.push({
        ...layer,
        dependantLayer: WELL_LAYER,
        isChecked,
        isMoving: false,
        layerId: WELL_LAYER_POINT,
        name: WELL_LAYER_POINT,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });
    }

    if (layerName === TYPE_LOG_LAYER) {
      newList.push({
        ...layer,
        dependantLayer: TYPE_LOG_LAYER,
        isChecked,
        isMoving: false,
        layerId: TYPE_LOG_VERTICAL_LAYER,
        name: TYPE_LOG_VERTICAL_LAYER,
        order: undefined,
        zoomLevel: 8,
        originalOrder: undefined
      });
    }
  }
}
